import { useSelector } from "react-redux";
import type { PillAccent } from "@financeable-com-au/financeable-ui";
import { useCalculateApplication } from "@/hooks/useCalculateApplication";
import { fCurrencyDecimal, fPercent } from "@/utils/formatNumber";
import { FeeFrequency, RepaymentFrequency } from "@/store/slices/types/applicationFormSlice";
import type { ApplicationFormSlice, LoanDetails, Fee } from "@/store/slices/types/applicationFormSlice";
import {
  loanDetailsSelector,
  // @ts-expect-error: ignoring as it is existing js file
} from "@/store/slices/applicationFormSlice";
import {
  sortLoadingList,
  getFeesList,
  getLoadingList,
  getTotalFeesByFrequency,
  getDocumentPillColour,
  getWeeklyRepaymentsHavingMonthly,
  getRepaymentFrequencyLabel,
} from "@/utils/lenderUtils";
import type { LoadingItem, LenderProduct } from "@/providers/LenderCardContextProvider/types/LenderProductTypes";

/**
 * Types.
 */
interface LoadingItemFormatted extends LoadingItem {
  formattedValue: string;
}

interface FeeFormatted extends Fee {
  formattedValue: string;
  result: boolean;
}

interface Document {
  name: string;
  colour: PillAccent;
}

interface CommonValue {
  value: number | undefined;
  formattedValue: string | undefined;
}

interface Range {
  min: CommonValue;
  max: CommonValue;
}

interface UseLenderCardResult {
  repayments: {
    monthly: CommonValue;
    weekly: CommonValue;
    monthlyRange?: Range | undefined;
    weeklyRange?: Range | undefined;
  };
  rate: {
    default: CommonValue;
    defaultRange?: Range | undefined;
    effective: CommonValue;
    effectiveRange?: Range | undefined;
    comparison: CommonValue;
    comparisonRange?: Range | undefined;
  };
  fees: {
    totalMonthly: CommonValue;
    totalUpfront: CommonValue;
    totalFinanced: CommonValue;
    allFees?: FeeFormatted[] | undefined;
    application: FeeFormatted[];
    maxOriginationFeeSet: string | undefined;
  };
  loanAmount: CommonValue;
  financeAmount: CommonValue;
  brokerage: {
    percentage: CommonValue;
    amount: CommonValue;
    max: CommonValue;
  };
  documents?: Document[];
  loadingList: LoadingItemFormatted[];
  infoList: string[];
}

const defaultCommonValue: CommonValue = {
  value: undefined,
  formattedValue: "",
};

const defaultResult: UseLenderCardResult = {
  repayments: {
    monthly: defaultCommonValue,
    weekly: defaultCommonValue,
  },
  rate: {
    default: defaultCommonValue,
    effective: defaultCommonValue,
    comparison: defaultCommonValue,
  },
  fees: {
    totalMonthly: defaultCommonValue,
    totalUpfront: defaultCommonValue,
    totalFinanced: defaultCommonValue,
    allFees: [],
    application: [],
    maxOriginationFeeSet: undefined,
  },
  loanAmount: defaultCommonValue,
  financeAmount: defaultCommonValue,
  brokerage: {
    percentage: defaultCommonValue,
    amount: defaultCommonValue,
    max: defaultCommonValue,
  },
  documents: [],
  loadingList: [],
  infoList: [],
};

interface UseLenderCardProps {
  product: LenderProduct | undefined;
  isInArrears: boolean;
}

const useLenderCard = ({ product, isInArrears }: UseLenderCardProps): UseLenderCardResult => {
  const {
    getFeeAmount,
    calculateQuoteRepayments,
    getBalloonAmount,
    getLoanAmount,
    getFinanceAmount,
    getEffectiveRate,
    getComparisonRate,
    getValuesToEvaluateCriteriaAgainst,
  } = useCalculateApplication();
  const { balloon, originationFee: applicationOriginationFee } = useSelector<ApplicationFormSlice, LoanDetails>(
    loanDetailsSelector,
  );

  if (!product) {
    return defaultResult;
  }

  const {
    rate,
    rateRange,
    fees: lenderFees,
    allFees,
    brokerage,
    brokerageAmount,
    brokerageMax,
    labels,
    loading,
    info,
  } = product;

  // Fees.
  // If origination fee set in the application is less than the max that
  // has been set by the lender, then use the application's origination fee.
  const fees = lenderFees
    .map((fee) => {
      if (fee.calc === "percent") {
        const feeAmount = getFeeAmount({ feePercentage: fee.value ?? 0 });
        return { ...fee, value: feeAmount };
      }
      return fee;
    })
    .map((fee) => {
      if (fee.name.toUpperCase() === "ORIGINATION FEE" && applicationOriginationFee < (fee.value ?? 0)) {
        return { ...fee, value: applicationOriginationFee };
      }
      return fee;
    });

  // We pass in the application values to evaluate the criteria against the fees.
  const applicationValues = getValuesToEvaluateCriteriaAgainst();
  const feesList = getFeesList(fees, applicationValues);
  const lendersFeesList = getFeesList(lenderFees, applicationValues);
  const lendersAllFeesList = getFeesList(allFees, applicationValues);
  const maxOriginationFeeSet = lendersFeesList.find(
    (fee) => fee.name.toUpperCase() === "ORIGINATION FEE",
  )?.formattedValue;
  // Monthly fees.
  const totalMonthlyFees = getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Monthly });
  const formattedTotalMonthlyFees = totalMonthlyFees ? fCurrencyDecimal(totalMonthlyFees) : undefined;
  // Upfront fees.
  const totalUpfrontFees = getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Upfront });
  const formattedTotalUpfrontFees = fCurrencyDecimal(totalUpfrontFees);
  // Financed fees.
  const totalFinancedFees = getTotalFeesByFrequency({ fees, frequency: FeeFrequency.Financed });
  const formattedTotalFinancedFees = fCurrencyDecimal(totalFinancedFees);
  // Loan amount.
  const loanAmount = getLoanAmount({ totalFinancedFees });
  const formattedLoanAmount = fCurrencyDecimal(loanAmount);
  // Finance amount.
  const financeAmount = getFinanceAmount({
    totalFinancedFees,
    brokerageAmount,
  });
  const formattedFinanceAmount = fCurrencyDecimal(financeAmount);
  // Brokerage.
  const formattedBrokerage = fPercent(brokerage ?? 0);
  const formattedBrokerageAmount = fCurrencyDecimal(brokerageAmount);
  const formattedBrokerageMax = fPercent(brokerageMax);
  // Document pill.
  const documents = labels.map((label) => ({
    name: label,
    colour: getDocumentPillColour[label as keyof typeof getDocumentPillColour],
  }));
  // Extras.
  const loadingList = getLoadingList(sortLoadingList(loading?.loadingList));
  const infoList = info;

  // Repayments.
  const repayments = calculateQuoteRepayments({
    financeAmount,
    balloon,
    rate,
    isInArrears,
  });
  const monthlyRepayments = repayments + totalMonthlyFees;
  const formattedMonthlyRepayments = `${fCurrencyDecimal(monthlyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Monthly)}`;
  const weeklyRepayments = getWeeklyRepaymentsHavingMonthly({
    monthlyRepaymentsValue: monthlyRepayments,
  });
  const formattedWeeklyRepayments = `${fCurrencyDecimal(weeklyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Weekly)}`;

  // Rate.
  const ratePercentage = fPercent(rate);
  const balloonAmount = getBalloonAmount({ balloonPercentage: balloon });

  // Effective rate.
  const effectiveRate = getEffectiveRate({
    repayments,
    totalFinancedFees,
    balloonAmount,
    rate,
    isInArrears,
  });
  const effectiveRateValue = Number(effectiveRate.toFixed(2));
  const formattedEffectiveRate = fPercent(effectiveRateValue);

  // Comparison rate.
  const comparisonRate = getComparisonRate({
    repayments,
    financeAmount,
    totalUpfrontFees,
    totalFinancedFees,
    brokerageAmount,
    balloonAmount,
    rate,
    isInArrears,
  });
  const formattedComparisonRate = fPercent(Number(comparisonRate.toFixed(2)));

  // Rate range.
  // Which affects default rate, repayments, effective rate and comparison rate.
  let repaymentsMonthlyRange: Range | undefined = undefined;
  let repaymentsWeeklyRange: Range | undefined = undefined;
  let defaultRateRange: Range | undefined = undefined;
  let effectiveRateRange: Range | undefined = undefined;
  let comparisonRateRange: Range | undefined = undefined;
  // If product has a rate range, then calculate the min and max repayments.
  if (rateRange) {
    // Min repayments.
    const minRepayments = calculateQuoteRepayments({
      financeAmount,
      balloon,
      rate: rateRange.min, // Min rate.
      isInArrears,
    });
    const minMonthlyRepayments = minRepayments + totalMonthlyFees;
    const formattedMinMonthlyRepayments = `${fCurrencyDecimal(minMonthlyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Monthly)}`;
    const minWeeklyRepayments = getWeeklyRepaymentsHavingMonthly({
      monthlyRepaymentsValue: minMonthlyRepayments,
    });
    const formattedMinWeeklyRepayments = `${fCurrencyDecimal(minWeeklyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Weekly)}`;
    // Max repayments.
    const maxRepayments = calculateQuoteRepayments({
      financeAmount,
      balloon,
      rate: rateRange.max, // Max rate.
      isInArrears,
    });
    const maxMonthlyRepayments = maxRepayments + totalMonthlyFees;
    const formattedMaxMonthlyRepayments = `${fCurrencyDecimal(maxMonthlyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Monthly)}`;
    const maxWeeklyRepayments = getWeeklyRepaymentsHavingMonthly({
      monthlyRepaymentsValue: maxMonthlyRepayments,
    });
    const formattedMaxWeeklyRepayments = `${fCurrencyDecimal(maxWeeklyRepayments)}${getRepaymentFrequencyLabel(RepaymentFrequency.Weekly)}`;
    // Assignment of repayments range.
    repaymentsMonthlyRange = {
      min: {
        value: minMonthlyRepayments,
        formattedValue: formattedMinMonthlyRepayments,
      },
      max: {
        value: maxMonthlyRepayments,
        formattedValue: formattedMaxMonthlyRepayments,
      },
    };
    repaymentsWeeklyRange = {
      min: {
        value: minWeeklyRepayments,
        formattedValue: formattedMinWeeklyRepayments,
      },
      max: {
        value: maxWeeklyRepayments,
        formattedValue: formattedMaxWeeklyRepayments,
      },
    };

    // Default rate range.
    // Rate.
    defaultRateRange = {
      min: {
        value: rateRange.min,
        formattedValue: fPercent(rateRange.min),
      },
      max: {
        value: rateRange.max,
        formattedValue: fPercent(rateRange.max),
      },
    };

    // Effective rate range.
    const minEffectiveRate = getEffectiveRate({
      repayments: minRepayments,
      totalFinancedFees,
      balloonAmount,
      rate: rateRange.min,
      isInArrears,
    });
    const minEffectiveRateValue = Number(minEffectiveRate.toFixed(2));
    const maxEffectiveRate = getEffectiveRate({
      repayments: maxRepayments,
      totalFinancedFees,
      balloonAmount,
      rate: rateRange.max,
      isInArrears,
    });
    const maxEffectiveRateValue = Number(maxEffectiveRate.toFixed(2));
    // Assignment of effective rate range.
    effectiveRateRange = {
      min: {
        value: minEffectiveRateValue,
        formattedValue: fPercent(minEffectiveRateValue),
      },
      max: {
        value: maxEffectiveRateValue,
        formattedValue: fPercent(maxEffectiveRateValue),
      },
    };

    // Comparison rate range.
    const minComparisonRate = getComparisonRate({
      repayments: minRepayments,
      financeAmount,
      totalUpfrontFees,
      totalFinancedFees,
      brokerageAmount,
      balloonAmount,
      rate: rateRange.min,
      isInArrears,
    });
    const minComparisonRateValue = Number(minComparisonRate.toFixed(2));
    const maxComparisonRate = getComparisonRate({
      repayments: maxRepayments,
      financeAmount,
      totalUpfrontFees,
      totalFinancedFees,
      brokerageAmount,
      balloonAmount,
      rate: rateRange.max,
      isInArrears,
    });
    const maxComparisonRateValue = Number(maxComparisonRate.toFixed(2));
    // Assignment of comparison rate range.
    comparisonRateRange = {
      min: {
        value: minComparisonRateValue,
        formattedValue: fPercent(minComparisonRateValue),
      },
      max: {
        value: maxComparisonRateValue,
        formattedValue: fPercent(maxComparisonRateValue),
      },
    };
  }

  return {
    repayments: {
      monthly: {
        value: monthlyRepayments,
        formattedValue: formattedMonthlyRepayments,
      },
      weekly: {
        value: weeklyRepayments,
        formattedValue: formattedWeeklyRepayments,
      },
      monthlyRange: repaymentsMonthlyRange,
      weeklyRange: repaymentsWeeklyRange,
    },
    rate: {
      default: {
        value: rate,
        formattedValue: ratePercentage,
      },
      defaultRange: defaultRateRange,
      effective: {
        value: effectiveRateValue,
        formattedValue: formattedEffectiveRate,
      },
      effectiveRange: effectiveRateRange,
      comparison: {
        value: comparisonRate,
        formattedValue: formattedComparisonRate,
      },
      comparisonRange: comparisonRateRange,
    },
    fees: {
      totalMonthly: {
        value: totalMonthlyFees,
        formattedValue: formattedTotalMonthlyFees,
      },
      totalUpfront: {
        value: totalUpfrontFees,
        formattedValue: formattedTotalUpfrontFees,
      },
      totalFinanced: {
        value: totalFinancedFees,
        formattedValue: formattedTotalFinancedFees,
      },
      allFees: lendersAllFeesList,
      application: feesList,
      maxOriginationFeeSet,
    },
    loanAmount: {
      value: loanAmount,
      formattedValue: formattedLoanAmount,
    },
    financeAmount: {
      value: financeAmount,
      formattedValue: formattedFinanceAmount,
    },
    brokerage: {
      percentage: {
        value: brokerage,
        formattedValue: formattedBrokerage,
      },
      amount: {
        value: brokerageAmount,
        formattedValue: formattedBrokerageAmount,
      },
      max: {
        value: brokerageMax,
        formattedValue: formattedBrokerageMax,
      },
    },
    documents,
    loadingList,
    infoList,
  };
};

export { useLenderCard, defaultResult as defaultLenderCard };
export type { FeeFormatted, UseLenderCardResult, CommonValue };
